import type { EffectiveTradeStatus, TradeItem, TradeStatus } from '@/types';
import { getEffectiveTradeStatus, type EffectiveStatusInput } from './effective-status';

/**
 * Pure helper functions for Phase 4 inline price editing logic.
 *
 * These are deterministic, DOM-free, float-safe utilities consumed by
 * Plan 04-02's MtmTable/MtmPage. No side effects, no store access.
 */

// ---------------------------------------------------------------------------
// resolveDefaultPrice
// ---------------------------------------------------------------------------

/**
 * Resolve the default (pre-fill) price for a position's New Price input.
 *
 * Pre-fill = COB (T-1 close from TickDB daily candles) when available, else
 * the gross/booking price as a graceful fallback (LR-393; proto-feedback
 * 2026-05-22: "for MTM in New Price suggest previous trading day's close").
 *
 * The previous prefill used `latest_mark_price_raw` — that was cyclic: each
 * MTM run pre-filled with the operator's most recent mark, so they were
 * effectively re-marking against their own prior mark instead of against
 * market COB. The current rule reads `cob_price_raw` (per-symbol, refreshed
 * daily; null only when TickDB has no published candle).
 *
 * Returns the API-original decimal string to preserve trailing-zero precision
 * (e.g. "152.50" stays "152.50", not "152.5").
 */
export function resolveDefaultPrice(position: TradeItem): string {
  return position.cob_price_raw ?? position.gross_price_raw;
}

// ---------------------------------------------------------------------------
// classifyPriceInput
// ---------------------------------------------------------------------------

/**
 * The classification result for a New Price input value.
 *
 * - empty: blank / whitespace-only — caller ignores / shows placeholder
 * - invalid: non-numeric or <= 0 — caller shows red border
 * - default: numerically equal to the pre-fill default (within epsilon) — no override written
 * - override: valid positive value that differs from default — caller writes to priceOverrides
 */
export type PriceInputClass =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'default' }
  | { kind: 'override'; value: string };

/**
 * Float-tolerance threshold for equality comparison.
 * Prevents '10.50' vs '10.5' from being classified as an override.
 */
const PRICE_EPSILON = 0.000001;

/**
 * Classify a raw price input string relative to the position's default price.
 *
 * This is the criterion #1 "clicking-in-without-typing is a no-op" rule:
 * an input pre-filled with the default that the user has not changed will
 * classify as 'default', so the caller does NOT write to priceOverrides.
 * Only an actual value change (parsed numeric difference > PRICE_EPSILON)
 * produces 'override'.
 *
 * @param raw - Raw string from the <input> element
 * @param defaultPrice - The resolved default price string (from resolveDefaultPrice)
 */
export function classifyPriceInput(raw: string, defaultPrice: string): PriceInputClass {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed) || parsed <= 0) return { kind: 'invalid' };
  if (Math.abs(parsed - Number(defaultPrice)) < PRICE_EPSILON) return { kind: 'default' };
  return { kind: 'override', value: trimmed };
}

// ---------------------------------------------------------------------------
// computeDelta
// ---------------------------------------------------------------------------

/**
 * Compute the absolute and percentage delta of an effective price against a baseline.
 *
 * abs = effectivePrice − baselinePrice
 * pct = (abs / baselinePrice) * 100   (guarded against divide-by-zero)
 *
 * Caller chooses the baseline: MtmTable's Δ column passes the MOVING baseline
 * (mtmBaselinePriceRaw = latest_mark_price_raw ?? gross_price_raw) so it matches
 * computeMtmExposure / computeMtmDialogDelta. Sign is preserved: a lower effective
 * price yields negative abs/pct.
 */
export function computeDelta(effectivePrice: number, baselinePrice: number): { abs: number; pct: number } {
  const abs = effectivePrice - baselinePrice;
  const pct = baselinePrice !== 0 ? (abs / baselinePrice) * 100 : 0;
  return { abs, pct };
}

// ---------------------------------------------------------------------------
// isEligibleStatus
// ---------------------------------------------------------------------------

/**
 * RAW-status gate for the repo cash-pool margin call — mirrors the backend
 * `_ELIGIBLE_STATUSES` (apps/positions/services/repo_margin_call.py).
 *
 * NOT for stock rows: their MTM eligibility is `isMtmEligibleRow`, which keys off the
 * EFFECTIVE status (LR-608).
 */
export function isEligibleStatus(status: TradeStatus): boolean {
  return status === 'active' || status === 'matured';
}

// ---------------------------------------------------------------------------
// isMtmEligibleRow / isMtmDefaultUnchecked (LR-608)
// ---------------------------------------------------------------------------

/**
 * Enumerated, NOT derived from `isEligibleStatus`: a pending partial recall is effectively
 * `pending_partial_settlement`, outside the raw active/matured set, so deriving this list
 * would silently drop it from the run and revert LR-388.
 */
const MTM_MARKABLE_STATUSES: readonly EffectiveTradeStatus[] = [
  'active',
  'pending_partial_settlement',
  'matured',
  'partially_settled',
];

/**
 * Markable, but excluded by the first-load pass and carved out of the header select-all —
 * including one is an explicit per-row click (matured: audit N4; partially settled: LR-608).
 *
 * `pending_partial_settlement` is deliberately ABSENT — it stays included by default and
 * marks `remaining_quantity` (LR-388).
 */
const MTM_DEFAULT_UNCHECKED_STATUSES: readonly EffectiveTradeStatus[] = [
  'matured',
  'partially_settled',
];

/** Whether a row may be ticked into a stock MTM run at all (effective status, not raw). */
export function isMtmEligibleRow(item: EffectiveStatusInput): boolean {
  return MTM_MARKABLE_STATUSES.includes(getEffectiveTradeStatus(item));
}

/** Whether a markable row arrives UNCHECKED (still tickable per-row). */
export function isMtmDefaultUnchecked(item: EffectiveStatusInput): boolean {
  return MTM_DEFAULT_UNCHECKED_STATUSES.includes(getEffectiveTradeStatus(item));
}

// ---------------------------------------------------------------------------
// isMtmMarkable
// ---------------------------------------------------------------------------

/**
 * Whether a position can be MARKED (repriced) in the bulk MTM run.
 *
 * Markable = MTM-eligible effective status (`isMtmEligibleRow`) AND NOT a repo/reverse_repo
 * trade. Per the customer "Repricing - MtM.xlsx" ("Repo Trades" section), repo
 * contracts are NOT repriced/marked individually — they are handled by a
 * per-counterparty cash-pool margin call (LR-385). Repo rows stay VISIBLE on the
 * MTM page (for exposure context) but must never enter the bulk_mark payload, the
 * included-count, or the Run-MTM dialog delta — otherwise clicking "Run MTM" would
 * submit a meaningless mark on a repo position.
 *
 * Single source of truth for "is this row part of the MTM run", consumed by
 * MtmPage (includedPositions + eligibleIds) and MtmTable (row checkbox).
 */
export function isMtmMarkable(
  item: EffectiveStatusInput & Pick<TradeItem, 'trade_type'>,
): boolean {
  return (
    isMtmEligibleRow(item) &&
    item.trade_type !== 'repo' &&
    item.trade_type !== 'reverse_repo'
  );
}

// ---------------------------------------------------------------------------
// isRepoCallable
// ---------------------------------------------------------------------------

/**
 * Whether a row is part of the repo margin call the page can actually post (LR-548, r2 review #1).
 *
 * Callable = a repo/reverse_repo trade + eligible status (`active` | `matured`). The mirror of
 * `isMtmMarkable` for the repo bucket: single source of truth for "is this row in the cash-pool
 * margin call", consumed by MtmPage (selection universes + book count), MtmTable (row checkbox,
 * header select-all, footer totals) and the CSV export. The trade's currency does not matter: the
 * call is in the GMRA agreement's currency and every trade is converted into it at COB.
 */
export function isRepoCallable(item: Pick<TradeItem, 'status' | 'trade_type'>): boolean {
  if (item.trade_type !== 'repo' && item.trade_type !== 'reverse_repo') return false;
  return isEligibleStatus(item.status);
}

/** The counterparty's margin-call scope. */
export interface RepoCallScope {
  /** The counterparty has no GMRA agreement → NOTHING is callable. */
  noGmraAgreement?: boolean;
}

/**
 * `isRepoCallable` under the counterparty's actual call scope — the ONLY predicate any
 * consumer of "is this row in the repo margin call?" should use (LR-548 r4: the checkbox,
 * the header select-all, the selection, the book count, the footer totals, the CSV basis and
 * the CSV's synthetic figures all have to agree, and a row nobody can call must appear in
 * none of them).
 */
export function isRepoCallableInScope(
  item: Pick<TradeItem, 'status' | 'trade_type'>,
  scope?: RepoCallScope,
): boolean {
  if (scope?.noGmraAgreement) return false;
  return isRepoCallable(item);
}
